import { Button, Popover, Select, Tooltip } from 'antd';
import { CloseOutlined, GroupOutlined, HolderOutlined } from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { FieldLike } from './types';
import type { GroupingLevelLike } from './groupTree';

export interface GroupingPopoverProps {
  levels: GroupingLevelLike[];
  fields: FieldLike[];
  onAdd: (fieldSlug: string) => void;
  onRemove: (fieldSlug: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onChangeOrder: (fieldSlug: string, order: 'name' | 'count') => void;
  onClearAll: () => void;
  /** Какие fieldType допустимы для группировки. По умолчанию select/multi_select/boolean/reference. */
  groupableTypes?: string[];
}

function SortableGroupLevel({
  level,
  fieldName,
  onRemove,
  onChangeOrder,
}: {
  level: GroupingLevelLike;
  fieldName: string;
  onRemove: () => void;
  onChangeOrder: (order: 'name' | 'count') => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: level.fieldSlug,
  });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="lens-group-row">
      <span {...attributes} {...listeners} className="lens-group-row__handle">
        <HolderOutlined />
      </span>
      <span className="lens-group-row__name">{fieldName}</span>
      <Select
        size="small"
        variant="borderless"
        style={{ width: 100 }}
        value={level.order}
        onChange={onChangeOrder}
        options={[
          { value: 'name', label: 'A→Я' },
          { value: 'count', label: '# кол-во' },
        ]}
      />
      <Button
        size="small"
        type="text"
        icon={<CloseOutlined style={{ fontSize: 10 }} />}
        onClick={onRemove}
        className="lens-group-row__remove"
      />
    </div>
  );
}

const DEFAULT_GROUPABLE_TYPES = ['select', 'multi_select', 'boolean', 'reference', 'user'];

export function GroupingPopover({
  levels,
  fields,
  onAdd,
  onRemove,
  onReorder,
  onChangeOrder,
  onClearAll,
  groupableTypes = DEFAULT_GROUPABLE_TYPES,
}: GroupingPopoverProps): JSX.Element {
  const groupableFields = fields.filter((f) => groupableTypes.includes(f.fieldType));
  const availableFields = groupableFields.filter(
    (f) => !levels.some((l) => l.fieldSlug === f.slug),
  );

  const groupDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = levels.findIndex((l) => l.fieldSlug === active.id);
    const newIdx = levels.findIndex((l) => l.fieldSlug === over.id);
    if (oldIdx !== -1 && newIdx !== -1) onReorder(oldIdx, newIdx);
  };

  const fieldNameMap = new Map(fields.map((f) => [f.slug, f.name]));

  const content = (
    <div className="lens-group-popover">
      {levels.length > 0 && (
        <DndContext
          sensors={groupDndSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleGroupDragEnd}
        >
          <SortableContext
            items={levels.map((l) => l.fieldSlug)}
            strategy={verticalListSortingStrategy}
          >
            {levels.map((level) => (
              <SortableGroupLevel
                key={level.fieldSlug}
                level={level}
                fieldName={fieldNameMap.get(level.fieldSlug) ?? level.fieldSlug}
                onRemove={() => onRemove(level.fieldSlug)}
                onChangeOrder={(order) => onChangeOrder(level.fieldSlug, order)}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
      {availableFields.length > 0 && (
        <Select
          size="small"
          className="lens-group-popover__add-select"
          style={{ width: '100%' }}
          placeholder={levels.length === 0 ? 'Группировать по…' : '+ Добавить уровень'}
          value={undefined}
          onChange={(slug) => {
            if (slug) onAdd(slug);
          }}
          options={availableFields.map((f) => ({ value: f.slug, label: f.name }))}
        />
      )}
      {levels.length > 0 && (
        <div className="lens-group-popover__footer">
          <Button
            size="small"
            type="text"
            danger
            onClick={onClearAll}
            className="lens-group-popover__clear"
          >
            Убрать группировку
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Popover content={content} title="Группировка" trigger="click" placement="bottomRight">
      <Tooltip title="Группировка">
        <Button
          icon={<GroupOutlined />}
          size="small"
          type={levels.length > 0 ? 'primary' : 'default'}
          data-testid="grouping-button"
          aria-label="Группировка"
        >
          {levels.length > 0 ? `${levels.length}` : undefined}
        </Button>
      </Tooltip>
    </Popover>
  );
}
